import React, {useEffect, useState} from "react";
import {UserIcon} from "@heroicons/react/24/solid";
import styles from "./AdminClientInfo.module.css";
import Button from "../../../common/button/Button";
import {GetAdminClientInfoResponseData} from "../../../../types/admin";
import {adminClientDeleteAPI, getClientInfoAPI, resetClientPasswordAPI} from "../../../../services/admin";
import ConfirmClientPopUp from "../../../../views/admin/popup/ConfirmClientPopUp";
import {useNavigate} from "react-router-dom";
import BusinessLicensePopUp from "../../../../views/admin/popup/BusinessLicensePopUp";
import {useAlert} from "../../../common/error/AlertContext";

interface AdminClientInfoProps {
    clientId: number;
}

//어드민 클라이언트 정보
const AdminClientInfo: React.FC<AdminClientInfoProps> = ({clientId}) => {
    const {showAlert} = useAlert();
    const {showConfirm} = useAlert();
    const [data, setData] = useState<GetAdminClientInfoResponseData | null>(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLicenseOpen, setIsLicenseOpen] = useState(false);
    const navigate = useNavigate();

    //클라이언트 계정 처리 팝업 닫기
    const handleCloseModal = () => {
        setIsModalOpen(false);
        fetchData();
    }

    //클라이언트 사업자등록증 팝업 닫기
    const handleCloseLicenseModal = () => {
        setIsLicenseOpen(false);
        fetchData();
    }

    //클라이언트 수정 페이지
    const handleEdit = () => {
        navigate(`/admin/clients/account/${clientId}/edit`);
    };

    //비밀번호 초기화
    const handelReset = async () => {
        //확인창
        showConfirm(
            '비밀번호를 초기화 하시겠습니까?',
            async () => {
                try {
                    //비밀번호 초기화 API
                    await resetClientPasswordAPI(clientId);
                    showAlert('비밀번호가 초기화 되었습니다.', "info");
                } catch (error) {
                    console.error('Reset failed:', error);
                }
            }
        )
    };

    //계정 삭제
    const handleDelete = async () => {
        //확인창
        showConfirm(
            '클라이언트 계정을 삭제하시겠습니까?',
            async () => {
                try {
                    const ids = new Set<number>();
                    ids.add(clientId);
                    //계정 삭제 API
                    await adminClientDeleteAPI(ids);
                    showAlert('클라이언트 계정이 삭제되었습니다.', "info");
                    //삭제 후 계정 테이블 페이지로 이동
                    navigate('/admin/clients/account')
                } catch (error) {
                    console.error('Delete failed:', error);
                }
            }
        )
    };

    //계정 처리 팝업 열기
    const handleOpenModal = () => {
        setIsModalOpen(true);
    }
    //사업자등록증 팝업 열기
    const handleOpenLicenseModal = () => {
        setIsLicenseOpen(true);
    }

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //클라이언트 정보 조회 API 호출
            const response = await getClientInfoAPI(clientId);
            setData(response);
        } catch (error) {
            console.error('Error fetching account:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, [clientId]);

    //날짜 없을 경우 '-' 출력
    const formatDate = (date: Date | undefined) => {
        if (!date) return '-';
        return `${date}`;
    };

    if (loading || !data) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.cardWrapper}>
                <div className={styles.accountCard}>
                    <div className={styles.header}>
                        <div className={styles.headerContent}>
                            <div className={styles.titleSection}>
                                <UserIcon className={styles.userIcon}/>
                                <h1 className={styles.title}>계정 정보</h1>
                            </div>
                            <div className={styles.buttons}>
                                {/*비밀번호 초기화*/}
                                <Button onClick={handelReset} variant="primary">
                                    PW 초기화
                                </Button>
                                {/*계정 정보 수정*/}
                                <Button onClick={handleEdit} variant="primary">
                                    수정
                                </Button>
                                {/*계정 삭제*/}
                                <Button onClick={handleDelete} variant="primary">
                                    삭제
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.content}>
                        <div className={styles.gridContainer}>
                            <h3 className={styles.info}>기업 정보</h3>
                            <div className={styles.line}/>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>회사명</span>
                                    <span className={styles.value}>{data.companyName}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>대표자 성명</span>
                                    <span className={styles.value}>{data.name}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>사업자등록번호</span>
                                    <span className={styles.value}>{data.businessNumber}</span>
                                </div>
                                {/* 개인정보 동의한 경우에만 업태, 종목, 사업장 소재지 표시 */}
                                {data.optional && (
                                    <>
                                        <div className={styles.infoItem}>
                                            <span className={styles.label}>업태</span>
                                            <span className={styles.value}>{data.type}</span>
                                        </div>
                                        <div className={styles.infoItem}>
                                            <span className={styles.label}>종목</span>
                                            <span className={styles.value}>{data.item}</span>
                                        </div>
                                        <div className={styles.infoItem}>
                                            <span className={styles.label}>사업장 소재지</span>
                                            <span className={styles.value}>{data.address}</span>
                                        </div>
                                    </>
                                )}
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>사업자등록증</span>
                                    {/*클라이언트 사업자등록증 조회 버튼*/}
                                    <span className={styles.value}>
                                    <Button
                                        variant="primary"
                                        onClick={handleOpenLicenseModal}
                                    >
                                        보기
                                    </Button>
                                </span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.gridContainer}>
                            <h3 className={styles.info}>계정 정보</h3>
                            <div className={styles.line}/>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>ID</span>
                                    <span className={styles.value}>{data.userName}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>계정 상태</span>
                                    <span className={styles.value}>
                                    {data.clientStatus}
                                        {/*승인 상태 아닐 시 처리 버튼 출력*/}
                                        {data.clientStatus !== "승인" && data.clientStatus !== "만료" && (
                                            <Button
                                                variant="primary"
                                                onClick={handleOpenModal}
                                            >
                                                처리
                                            </Button>
                                        )}
                                </span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>생성일자</span>
                                    <span className={styles.value}>{formatDate(data.createdAt)}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>접속일자</span>
                                    <span className={styles.value}>{formatDate(data.accessedAt)}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>개인정보 수집·이용 동의 여부</span>
                                    <span className={styles.value}>{data.optional ? '동의' : '미동의'}</span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.gridContainer}>
                            <h3 className={styles.info}>담당자 정보</h3>
                            <div className={styles.line}/>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>담당자 성명</span>
                                    <span className={styles.value}>{data.managerName}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>담당자 전화번호</span>
                                    <span className={styles.value}>{data.phoneNumber}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>Email</span>
                                    <span className={styles.value}>{data.email}</span>
                                </div>
                                {/* 개인정보 동의한 경우에만 부서와 직위 표시 */}
                                {data.optional && (
                                    <>
                                        <div className={styles.infoItem}>
                                            <span className={styles.label}>담당자 부서</span>
                                            <span className={styles.value}>{data.department}</span>
                                        </div>
                                        <div className={styles.infoItem}>
                                            <span className={styles.label}>담당자 직위</span>
                                            <span className={styles.value}>{data.position}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/*클라이언트 계정 처리 팝업*/}
            <ConfirmClientPopUp
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                clientId={data?.clientId}
            />
            {/*사업자등록증 조회 팝업*/}
            <BusinessLicensePopUp
                isOpen={isLicenseOpen}
                onClose={handleCloseLicenseModal}
                clientId={data.clientId}
            />
        </div>
    );
}

export default AdminClientInfo;